import { useCallback, useMemo, useState } from "react";
import {
  countActiveFilters,
  DISCOVERY_SOURCE_OPTIONS,
  EMPTY_FILTERS,
  FAMILY_OPTIONS,
  type LeaderboardFilters,
  QUICK_FILTER_PRESETS,
  type QuickFilterId,
  RAISE_BUCKET_OPTIONS,
  SCORE_BAND_OPTIONS,
  SUBSCORE_KEYS,
  TOKEN_VERIFIED_OPTIONS,
  TREND_OPTIONS,
} from "../types/filters.ts";

const STORAGE_KEY = "lb-filters-v1";

/**
 * Drop any persisted values that are no longer in the current
 * frontend allowlists. Protects against:
 *
 *   1. A user with a stale `discovery_source=discovery_run` from before
 *      the 2026-05-11 allowlist correction — without this filter, the
 *      UI would render a chip ("Source: discovery_run") that sends a
 *      query the backend silently ignores.
 *   2. Hand-edited localStorage / schema drift between releases.
 *
 * Unknown values are stripped silently; valid values are retained.
 */
function normalizeStoredFilters(input: unknown): LeaderboardFilters {
  if (!input || typeof input !== "object" || Array.isArray(input)) return EMPTY_FILTERS;
  const raw = input as Record<string, unknown>;
  const out: Record<string, unknown> = {};

  const filterArray = <T extends string>(
    value: unknown,
    allowed: readonly T[] | null
  ): T[] | undefined => {
    if (!Array.isArray(value)) return undefined;
    const keep = allowed
      ? value.filter(
          (v): v is T => typeof v === "string" && (allowed as readonly string[]).includes(v)
        )
      : value.filter((v): v is T => typeof v === "string" && v.trim() !== "");
    return keep.length > 0 ? keep : undefined;
  };

  if (typeof raw.search === "string" && raw.search.trim()) out.search = raw.search;
  if (typeof raw.category === "string" && raw.category) out.category = raw.category;
  if (raw.archived === true) out.archived = true;
  if (typeof raw.scoreMin === "number" && Number.isFinite(raw.scoreMin))
    out.scoreMin = raw.scoreMin;
  if (typeof raw.scoreMax === "number" && Number.isFinite(raw.scoreMax))
    out.scoreMax = raw.scoreMax;
  const scoreBand = filterArray(raw.scoreBand, SCORE_BAND_OPTIONS);
  if (scoreBand) out.scoreBand = scoreBand;
  const family = filterArray(raw.family, FAMILY_OPTIONS);
  if (family) out.family = family;
  if (typeof raw.raiseMin === "number" && Number.isFinite(raw.raiseMin))
    out.raiseMin = raw.raiseMin;
  if (typeof raw.raiseMax === "number" && Number.isFinite(raw.raiseMax))
    out.raiseMax = raw.raiseMax;
  const raiseBucket = filterArray(raw.raiseBucket, RAISE_BUCKET_OPTIONS);
  if (raiseBucket) out.raiseBucket = raiseBucket;
  if (raw.raiseUnknown === true) out.raiseUnknown = true;
  if (typeof raw.trend === "string" && (TREND_OPTIONS as readonly string[]).includes(raw.trend)) {
    out.trend = raw.trend;
  }
  if (raw.hasBackers === true) out.hasBackers = true;
  if (typeof raw.tgeConfidenceMin === "number" && Number.isFinite(raw.tgeConfidenceMin)) {
    out.tgeConfidenceMin = raw.tgeConfidenceMin;
  }
  if (typeof raw.tgeConfidenceMax === "number" && Number.isFinite(raw.tgeConfidenceMax)) {
    out.tgeConfidenceMax = raw.tgeConfidenceMax;
  }
  if (raw.hasTgeHints === true) out.hasTgeHints = true;
  // hasToken is tri-state, both true and false are valid persisted states.
  if (raw.hasToken === true || raw.hasToken === false) out.hasToken = raw.hasToken;
  const tokenVerified = filterArray(raw.tokenVerified, TOKEN_VERIFIED_OPTIONS);
  if (tokenVerified) out.tokenVerified = tokenVerified;
  const ecosystem = filterArray(raw.ecosystem, null);
  if (ecosystem) out.ecosystem = ecosystem;
  if (typeof raw.auditedWithinDays === "number" && Number.isFinite(raw.auditedWithinDays)) {
    out.auditedWithinDays = raw.auditedWithinDays;
  }
  if (typeof raw.auditedOlderThanDays === "number" && Number.isFinite(raw.auditedOlderThanDays)) {
    out.auditedOlderThanDays = raw.auditedOlderThanDays;
  }
  const discoverySource = filterArray(raw.discoverySource, DISCOVERY_SOURCE_OPTIONS);
  if (discoverySource) out.discoverySource = discoverySource;
  if (
    typeof raw.subscoreKey === "string" &&
    (SUBSCORE_KEYS as readonly string[]).includes(raw.subscoreKey)
  ) {
    out.subscoreKey = raw.subscoreKey;
  }
  if (typeof raw.subscoreMin === "number" && Number.isFinite(raw.subscoreMin)) {
    out.subscoreMin = raw.subscoreMin;
  }

  return out as LeaderboardFilters;
}

function readFromStorage(): LeaderboardFilters {
  if (typeof localStorage === "undefined") return EMPTY_FILTERS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_FILTERS;
    return normalizeStoredFilters(JSON.parse(raw));
  } catch {
    return EMPTY_FILTERS;
  }
}

// Exported for tests.
export const __normalizeStoredFilters = normalizeStoredFilters;

function writeToStorage(filters: LeaderboardFilters): void {
  if (typeof localStorage === "undefined") return;
  try {
    if (Object.keys(filters).length === 0) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    }
  } catch {
    /* ignore quota / private-mode errors */
  }
}

export interface UseLeaderboardFiltersResult {
  filters: LeaderboardFilters;
  activeCount: number;
  setFilters: (
    next: LeaderboardFilters | ((prev: LeaderboardFilters) => LeaderboardFilters)
  ) => void;
  patchFilters: (patch: Partial<LeaderboardFilters>) => void;
  applyPreset: (id: QuickFilterId) => void;
  clearPreset: (id: QuickFilterId) => void;
  resetAll: () => void;
}

/**
 * Owns leaderboard filter state with localStorage persistence under the
 * `lb-filters-v1` key. Mirrors the existing `lb-sort-col` / `lb-sort-dir`
 * pattern used for sort persistence.
 */
export function useLeaderboardFilters(): UseLeaderboardFiltersResult {
  const [filters, setFiltersState] = useState<LeaderboardFilters>(() => readFromStorage());

  const setFilters = useCallback(
    (next: LeaderboardFilters | ((prev: LeaderboardFilters) => LeaderboardFilters)) => {
      setFiltersState((prev) => {
        const value = typeof next === "function" ? next(prev) : next;
        // Strip any undefined / empty-array / empty-string values so
        // the persisted blob stays small and `countActiveFilters`
        // doesn't double-count.
        //
        // `false` is dropped for plain two-state toggles
        // (`archived`, `hasBackers`, `hasTgeHints`, `raiseUnknown`) —
        // they have no meaning when off. `hasToken` is intentionally
        // tri-state (true = has token, false = no token,
        // undefined = don't filter) so its `false` value is preserved.
        const cleaned: LeaderboardFilters = {};
        for (const [k, v] of Object.entries(value)) {
          if (v == null) continue;
          if (Array.isArray(v) && v.length === 0) continue;
          if (typeof v === "string" && v.trim() === "") continue;
          if (v === false && k !== "hasToken") continue;
          (cleaned as Record<string, unknown>)[k] = v;
        }
        writeToStorage(cleaned);
        return cleaned;
      });
    },
    []
  );

  const patchFilters = useCallback(
    (patch: Partial<LeaderboardFilters>) => {
      setFilters((prev) => ({ ...prev, ...patch }));
    },
    [setFilters]
  );

  const applyPreset = useCallback(
    (id: QuickFilterId) => {
      const preset = QUICK_FILTER_PRESETS[id];
      if (!preset) return;
      // Presets replace the structured filters entirely (single-string
      // chip semantics). Search box is preserved across preset switches.
      // Disallow presets from clobbering search even if a future preset
      // sets one — the search box is user-driven only.
      const { search: _ignored, ...presetFilters } = preset.filters;
      setFilters((prev) => ({
        ...(prev.search ? { search: prev.search } : {}),
        ...presetFilters,
      }));
    },
    [setFilters]
  );

  /**
   * Clears just the keys that the given preset sets, leaving search
   * and any unrelated filter state intact. Used by the quick-filter
   * chip row when the user clicks an already-active chip — they
   * expect "toggle off this preset", not "reset everything".
   */
  const clearPreset = useCallback(
    (id: QuickFilterId) => {
      const preset = QUICK_FILTER_PRESETS[id];
      if (!preset) return;
      const keysToClear = Object.keys(preset.filters).filter((k) => k !== "search");
      setFilters((prev) => {
        const next: Record<string, unknown> = { ...prev };
        for (const k of keysToClear) delete next[k];
        return next as LeaderboardFilters;
      });
    },
    [setFilters]
  );

  const resetAll = useCallback(() => {
    setFilters(EMPTY_FILTERS);
  }, [setFilters]);

  const activeCount = useMemo(() => countActiveFilters(filters), [filters]);

  return {
    filters,
    activeCount,
    setFilters,
    patchFilters,
    applyPreset,
    clearPreset,
    resetAll,
  };
}
